import { Controller } from "@hotwired/stimulus";
// No need to import preline
// import * as preline from "preline"
import HSOverlay from "preline/dist/overlay.js";
import { appsignal } from "../appsignal";

export default class extends Controller {
  static targets = [
    "imageTemplate",
    "textTemplate",
    "instructionsModal",
    "instructionsNextButton",
    "question",
    "questionStats",
    "categoryStats",
    "brandButton",
    "brandsContainer"
  ];
  static values = {
    survey: Object,
    participationId: Number,
    answeredQuestionIds: Array,
    answeredCategoryIds: Array,
    questionnaireUrl: String,
    finishAnsweringUrl: String,
    translations: Object,
    batchSize: { type: Number, default: 5 },
  };

  #answerQueue = [];
  #acceptingInputs = false;
  #modalShown = false;
  #keepaliveFlushed = false;

  connect() {
    this.participationIdValue = this.participationIdValue || 0;

    if (!this.initializeSurveyState()) return;

    console.debug("Connected with participationId", this.participationIdValue);

    window.addEventListener("beforeunload", this.handleBeforeUnload);
    document.addEventListener("turbo:load", this.handleTurboLoad);
    this.addSurveyBreadcrumb("connect", "Survey controller initialized");
  }

  acceptInputs(state) {
    this.#acceptingInputs = state;
    this.brandButtonTargets.forEach((button) => {
      button.classList.toggle("pointer-events-none", !state);
      button.disabled = !state;
    });
  }

  answerQuestion(event) {
    event.preventDefault();
    if (!this.#acceptingInputs) return;

    const question = this.currentQuestion;
    const answer = {
      category_id: this.currentCategory.id,
      question_id: question.id,
      question_type: this.getQuestionType(this.currentCategory),
      brand_id: event.currentTarget.dataset.brandId,
      survey_id: this.surveyValue.id,
    };

    this.#answerQueue.push(answer);

    const ids = this.answeredQuestionIdsValue;
    ids.push(question.id);
    this.answeredQuestionIdsValue = ids;

    this.nextQuestion();

    if (this.#answerQueue.length >= this.batchSizeValue) {
      this.sendAnswerBatch();
    }
  }

  async sendAnswerBatch({ keepalive = false } = {}) {
    if (this.#answerQueue.length === 0) return;

    // Prevent double-flush: both disconnect() and handleBeforeUnload fire on the
    // same navigation. Without this guard, two keepalive requests would race and
    // potentially exceed the browser's 64KB aggregate keepalive budget.
    if (keepalive) {
      if (this.#keepaliveFlushed) return;
      this.#keepaliveFlushed = true;
    }

    const endpoint = "/answers/batch";
    const batch = [...this.#answerQueue];
    this.#answerQueue = []; // Clear queue immediately (optimistic)
    this.addSurveyBreadcrumb("send_answer_batch", "Attempting answer batch flush", {
      endpoint,
      batch_size: batch.length,
      keepalive,
    });

    // Use sendBeacon for page-unload scenarios: purpose-built for navigation,
    // no 64KB aggregate limit, and guaranteed delivery even as the page tears down.
    // CSRF is skipped on this endpoint (skip_before_action :verify_authenticity_token).
    if (keepalive) {
      const blob = new Blob([JSON.stringify({ answers: batch })], { type: "application/json" });
      const sent = navigator.sendBeacon(endpoint, blob);
      if (!sent) {
        this.#answerQueue.unshift(...batch);
        this.reportSurveyError(new Error("sendBeacon failed to queue request"), "sendAnswerBatch", {
          endpoint,
          keepalive,
          batch_size: batch.length,
          queue_restored: true,
        });
      }
      return;
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({ answers: batch }),
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`${endpoint} failed with ${response.status}`);
      }
      console.debug("Answer batch submitted successfully");
    } catch (error) {
      console.error("Error submitting answer batch:", error);

      // "Load failed" and AbortError are browser navigation aborts — the page is
      // unloading and the in-flight fetch was cancelled. Attempt sendBeacon as a
      // last-resort delivery and skip AppSignal reporting (not a real error).
      if (error.name === "AbortError" || error.message === "Load failed") {
        const blob = new Blob([JSON.stringify({ answers: batch })], { type: "application/json" });
        navigator.sendBeacon(endpoint, blob);
        return;
      }

      // If it fails for a real reason, put the batch back to be retried.
      this.#answerQueue.unshift(...batch);
      this.reportSurveyError(error, "sendAnswerBatch", {
        endpoint,
        keepalive,
        batch_size: batch.length,
        queue_restored: true,
      });
    }
  }

  initializeSurveyState() {
    this.remainingCategories = this.getRemainingCategories();

    if (this.remainingCategories.length === 0) {
      this.reportSurveyError(
        new Error("SurveyController failed to initialize without remaining categories"),
        "connect",
        { initialization_state: "missing_remaining_categories" },
      );
      return false;
    }

    this.currentCategory = this.getCategory(this.remainingCategories[0].id);

    if (!this.currentCategory) {
      this.reportSurveyError(
        new Error("SurveyController failed to initialize without a current category"),
        "connect",
        {
          initialization_state: "missing_current_category",
          initial_category_id: this.remainingCategories[0]?.id,
        },
      );
      return false;
    }

    this.remainingQuestions = this.getRemainingQuestions();
    return true;
  }

  surveyErrorTags(extra = {}) {
    return {
      survey_id: this.surveyValue?.id,
      participation_id: this.participationIdValue,
      current_category_id: this.currentCategory?.id,
      current_category_title: this.currentCategory?.title,
      current_question_id: this.currentQuestion?.id,
      queued_answers_count: this.#answerQueue.length,
      answered_questions_count: this.answeredQuestionIdsValue?.length,
      answered_categories_count: this.answeredCategoryIdsValue?.length,
      ...extra,
    };
  }

  reportSurveyError(error, action, extraTags = {}) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));

    appsignal.sendError(normalizedError, (span) => {
      span.setAction(`SurveyController#${action}`);
      span.setNamespace("frontend");
      span.setTags(this.surveyErrorTags(extraTags));
    });
  }

  addSurveyBreadcrumb(action, message, metadata = {}) {
    appsignal.addBreadcrumb({
      category: "survey",
      action,
      message,
      metadata,
    });
  }

  getQuestionType(category) {
    switch (category.category_type) {
      case "PhotoCategory":
        return "PhotoQuestion";
      case "TextCategory":
        return "TextQuestion";
    }
  }

  nextQuestion() {
    if (this.remainingQuestions.length === 0) {
      const categoryIds = this.answeredCategoryIdsValue;
      categoryIds.push(this.currentCategory.id);
      this.answeredCategoryIdsValue = categoryIds;
      this.nextCategory();
    } else {
      let nextQuestion = this.remainingQuestions.pop();
      this.currentQuestion = nextQuestion;
      this.renderQuestion(nextQuestion);
    }
    this.renderStats();
  }

  renderOutOf(remaining, total) {
    return `<span class="text-sm text-slate-700 text-nowrap">${remaining} / ${total}</span>`;
  }

  statusLabel(name) {
    return `<span class="text-xs uppercase text-slate-500">${name}</span>`;
  }

  renderStats() {
    this.questionStatsTarget.innerHTML =
      this.statusLabel(this.translationsValue.question) +
      " " +
      this.renderOutOf(
        this.currentCategory.questions.length - this.remainingQuestions.length,
        this.currentCategory.questions.length,
      );
    this.categoryStatsTarget.innerHTML =
      this.statusLabel(this.translationsValue.category) +
      " " +
      this.renderOutOf(
        this.surveyValue.categories.length -
        this.remainingCategories.length +
        1,
        this.surveyValue.categories.length,
      );
  }

  async nextCategory() {
    await this.sendAnswerBatch(); // Send any remaining answers

    console.debug("nextCategory from", this.remainingCategories.map(c => c.id), this.answeredCategoryIdsValue);
    const endpoint = "/category_completions";
    const nextCategoryModel = this.getRemainingCategories().shift();
    console.debug("nextCategory selected", nextCategoryModel);
    this.acceptInputs(false);

    if (nextCategoryModel) {
      this.addSurveyBreadcrumb("next_category", "Attempting category completion", {
        endpoint,
        completed_category_id: this.currentCategory?.id,
        next_category_id: nextCategoryModel.id,
      });

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          },
          body: JSON.stringify({
            category_completion: {
              category_id: this.currentCategory.id,
              survey_id: this.surveyValue.id,
            },
          }),
          credentials: "same-origin",
        });

        if (response.ok) {
          this.currentCategory = nextCategoryModel;
          this.answeredQuestionIdsValue = []; // Reset answered questions for new category
          this.remainingQuestions = this.getRemainingQuestions();
          this.remainingCategories = this.getRemainingCategories();
          this.showCategoryModal();
          this.nextQuestion();
        } else {
          console.error("Response not OK. Error completing category:", response);
          throw new Error(`${endpoint} failed with ${response.status}`);
        }
      } catch (error) {
        console.error("Error completing category:", error);
        this.reportSurveyError(error, "nextCategory", {
          endpoint,
          completed_category_id: this.currentCategory?.id,
          next_category_id: nextCategoryModel.id,
        });
        this.acceptInputs(true);
      }
    } else {
      console.debug("End of survey. Finished answering.");
      this.finishAnswering();
    }
  }

  async finishAnswering() {
    await this.sendAnswerBatch(); // Send any final answers

    // Submit finished_answering
    this.addSurveyBreadcrumb("finish_answering", "Attempting survey completion", {
      endpoint: this.finishAnsweringUrlValue,
      questionnaire_url: this.questionnaireUrlValue,
    });

    try {
      const response = await fetch(this.finishAnsweringUrlValue, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
        credentials: "same-origin",
      });

      if (response.ok) {
        this.gotoQuestionnaire();
      } else {
        throw new Error(`${this.finishAnsweringUrlValue} failed with ${response.status}`);
      }
    } catch (error) {
      console.error("Error finishing survey:", error);
      this.reportSurveyError(error, "finishAnswering", {
        endpoint: this.finishAnsweringUrlValue,
        questionnaire_url: this.questionnaireUrlValue,
      });
      this.acceptInputs(true);
    }
  }

  gotoQuestionnaire() {
    console.debug("gotoQuestionnaire");
    window.location.href = this.questionnaireUrlValue;
  }

  showCategoryModal() {
    this.acceptInputs(false);
    this.#modalShown = true;

    if (this.instructionsModalTarget) {
      this.instructionsModalTarget.getElementsByClassName("category-title")[0].innerHTML = this.currentCategory.title;
      this.instructionsModalTarget.getElementsByClassName("category-instructions")[0].innerHTML = this.currentCategory.instructions;
      this.instructionsNextButtonTarget.innerHTML = this.translationsValue.continue || "Continue";

      let modal = HSOverlay.getInstance(this.instructionsModalTarget, true);
      modal.element.on("close", () => {
        this.acceptInputs(true)
        this.#modalShown = false
      });
      HSOverlay.open(this.instructionsModalTarget);
    } else {
      console.debug("No instructions modal target");
    }
  }

  renderQuestion(question) {
    console.debug(
      "renderQuestion",
      question,
      this.currentCategory.category_type,
    );
    if (this.currentCategory.category_type === "PhotoCategory") {
      this.renderImageQuestion(question);
    } else {
      this.renderTextQuestion(question);
    }
    
    if (!this.#modalShown) this.acceptInputs(true)
  }

  getCategory(id) {
    return this.surveyValue.categories.find((c) => c.id == id);
  }

  getQuestion(id) {
    return this.currentCategory.questions.find((q) => q.id == id);
  }

  renderImageQuestion(question) {
    let template = this.imageTemplateTarget.content.cloneNode(true);
    let imageEl = template.querySelector(".image");
    imageEl.src = question.photo;
    if (question.photo2x) {
      imageEl.srcset = `${question.photo} 1x, ${question.photo2x} 2x`;
    }

    this.questionTarget.innerHTML = imageEl.outerHTML;
  }

  renderTextQuestion(question) {
    let template = this.textTemplateTarget.content.cloneNode(true);
    let textEl = template.querySelector(".text");
    textEl.innerHTML = question.content;

    this.questionTarget.innerHTML = textEl.outerHTML;
  }

  getRemainingCategories() {
    return this.surveyValue.categories.filter(
      (c) => !this.answeredCategoryIdsValue.includes(c.id),
    );
  }

  getRemainingQuestions() {
    let q = this.currentCategory.questions.filter(
      (q) => !this.answeredQuestionIdsValue.includes(q.id),
    );
    return this.shuffle(q);
  }

  shuffleBrands() {
    const container = this.brandsContainerTarget;
    const elements = this.brandButtonTargets;

    const shuffledElements = this.shuffle(Array.from(elements));
    const trashBrandIndex = shuffledElements.findIndex((element) => element.dataset.surveyTrashValue === "true");
    if (trashBrandIndex !== -1) {
      const trashBrand = shuffledElements.splice(trashBrandIndex, 1)[0];
      shuffledElements.push(trashBrand);
    }

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    shuffledElements.forEach((element) => {
      container.appendChild(element);
    });
  }

  shuffle(array) {
    let currentIndex = array.length, randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex > 0) {
      // Pick a remaining element.
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    document.removeEventListener("turbo:load", this.handleTurboLoad);

    if (this.#answerQueue.length > 0) {
      this.addSurveyBreadcrumb("disconnect", "Flushing answers on disconnect", {
        endpoint: "/answers/batch",
        queued_answers_count: this.#answerQueue.length,
        keepalive: true,
      });
      this.sendAnswerBatch({ keepalive: true });
    }
  }

  handleTurboLoad = () => {
    console.debug("turbo:load from survey_controller");

    let step = "showCategoryModal";

    try {
      this.showCategoryModal();
      step = "nextQuestion";
      this.nextQuestion();
      step = "shuffleBrands";
      this.shuffleBrands();
    } catch (error) {
      this.reportSurveyError(error, "turboLoad", { step });
    }
  }

  handleBeforeUnload = (event) => {
    try {
      if (this.#answerQueue.length > 0) {
        console.debug("handleBeforeUnload with answerQueue", this.#answerQueue);
        event.preventDefault();
        this.addSurveyBreadcrumb("before_unload", "Flushing answers before unload", {
          endpoint: "/answers/batch",
          queued_answers_count: this.#answerQueue.length,
          keepalive: true,
        });
        this.sendAnswerBatch({ keepalive: true });
      }
    } catch (error) {
      this.reportSurveyError(error, "beforeUnload", {
        queued_answers_count: this.#answerQueue.length,
      });
    }
  }
}
